import { FC, PropsWithChildren, useRef } from 'react'
import styles from './offer-card.module.scss'
import classNames from '@/utils/helpers/class-names.helper'
import Image from 'next/image'
import Button from '@/components/ui/Button'
import useHorizontalScroll from '@/hooks/horizontal-scroll.hook'
import useScrollXPagination from '@/hooks/scroll-x-pagination.hook'

type OfferCardProps = {
  as?: 'div' | 'li'
  title: string
  button?: {
    text: string
    href: string
  }
  bgSrc?: string
  price?: string
  className?: string
  bgClassName?: string
  innerClassName?: string
  light?: boolean
  gradient?: boolean
  dark?: boolean
}

type OfferCardWrapperProps = {
  className?: string
  scroll?: boolean
}

interface IOfferCard extends FC<PropsWithChildren<OfferCardProps>> {
  Wrapper: FC<PropsWithChildren<OfferCardWrapperProps>>
}

const CardBlock: FC<PropsWithChildren<{ as?: 'div' | 'li'; className?: string }>> = ({
  children,
  as,
  className,
}) => {
  if (as === 'div') {
    return <div className={classNames(styles.card, className)}>{children}</div>
  }

  return <li className={classNames(styles.card, className)}>{children}</li>
}

const OfferCard: IOfferCard = ({
  as = 'li',
  button,
  title,
  bgSrc,
  children,
  className,
  price,
  bgClassName,
  innerClassName,
  light,
  gradient,
  dark,
}) => {
  return (
    <CardBlock as={as} className={className}>
      <div
        className={classNames(
          styles.inner,
          innerClassName,
          light && styles._light,
          gradient && styles._gradient,
          dark && styles._dark
        )}
      >
        {bgSrc && (
          <div className={classNames(styles.bg, bgClassName, 'bg')}>
            <Image width={277} height={244} src={bgSrc} alt={title} title={title} loading="lazy" />
          </div>
        )}
        <p className={styles.title}>{title}</p>
        <div className={styles.text}>{children}</div>
        {price && <p className={styles.price}>{price}</p>}
        {button && (
          <Button as="a" href={button.href} className={styles.button} main>
            {button.text}
          </Button>
        )}
      </div>
    </CardBlock>
  )
}

const OfferCardWrapper: IOfferCard['Wrapper'] = ({ children, className, scroll }) => {
  const ref = useRef<HTMLUListElement>(null)

  useHorizontalScroll(ref)

  const { pagination } = useScrollXPagination(ref)

  return (
    <>
      <ul ref={ref} className={classNames(styles.wrapper, className, scroll && styles._scroll)}>
        {children}
      </ul>
      <div className={styles.pagination}>{pagination}</div>
    </>
  )
}

OfferCard.Wrapper = OfferCardWrapper

export default OfferCard
